import store from "../../store";
import {
  HubConnectionBuilder,
  HttpTransportType,
  LogLevel,
} from "@microsoft/signalr";

let connection = null;
let reconnectTimer = null;
const subscribedGameIds = new Set();

const getApiBaseUrl = () => {
  return store.apiBaseUrl || '';
};

const hubUrl = () => getApiBaseUrl() + '/hubs/crash';
const setConnectionState = (state) => {
  store.crash.connectionState = state;
  console.debug('CrashHubClient.connectionState', state);
};
const scheduleReconnect = () => {
  if (reconnectTimer) clearTimeout(reconnectTimer);
  reconnectTimer = setTimeout(() => startConnection(), 3000);
};

export const createCrashConnection = () => {
  connection = new HubConnectionBuilder()
    .withUrl(hubUrl(), {
      skipNegotiation: true,
      transport: HttpTransportType.WebSockets,
      accessTokenFactory: () => store.user?.access_token
    })
    .configureLogging(LogLevel.Information)
    .build();

  connection.onclose(() => {
    setConnectionState('disconnected');
    scheduleReconnect();
  });

  return connection;
};

export const startConnection = async () => {
  if (!connection) createCrashConnection();
  try {
    console.debug('CrashHubClient.startConnection token present', Boolean(store.user?.access_token));
    await connection.start();
    setConnectionState('connected');

    // Group membership is lost on reconnect; resubscribe active games.
    for (const gameId of subscribedGameIds) {
      try {
        await connection.invoke('SubscribeToGame', gameId);
      } catch (err) {
        console.warn('CrashHubClient resubscribe failed', gameId, err);
      }
    }

    return true;
  } catch (err) {
    setConnectionState('disconnected');
    scheduleReconnect();
    return false;
  }
};

const ensureConnection = async () => {
  if (!connection) await startConnection();
  return connection;
};

export const stopConnection = async () => {
  if (connection) {
    try { await connection.stop(); } catch (err) { console.warn('connection.stop failed', err); }
    connection = null;
    setConnectionState('disconnected');
  }
};

const invokeWithReconnect = async (methodName, ...args) => {
  if (!await ensureConnection()) return false;
  try {
    const res = await connection.invoke(methodName, ...args);
    console.debug('CrashHubClient.invoke result', methodName, res);
    return res !== false;
  } catch (e) {
    console.warn(methodName + ' invoke failed, attempting to restart connection', e);
    const started = await startConnection();
    if (!started) return false;
    try {
      const res2 = await connection.invoke(methodName, ...args);
      return res2 !== false;
    } catch (e2) {
      console.error(methodName + ' invoke failed after restart', e2);
      return false;
    }
  }
};

export const subscribeToGame = async (gameId) => {
  const numericGameId = Number(gameId);
  const ok = await invokeWithReconnect('SubscribeToGame', numericGameId);
  if (ok) subscribedGameIds.add(numericGameId);
  return ok;
};

export const unsubscribeFromGame = async (gameId) => {
  const numericGameId = Number(gameId);
  const ok = await invokeWithReconnect('UnsubscribeFromGame', numericGameId);
  if (ok) subscribedGameIds.delete(numericGameId);
  return ok;
};
export const invokeStop = (ticketIdentifier) => invokeWithReconnect('Stop', ticketIdentifier, store.user?.access_token);

export const on = (methodName, handler) => {
  if (!connection) createCrashConnection();
  connection.on(methodName, handler);
};

export default {
  createCrashConnection,
  startConnection,
  stopConnection,
  subscribeToGame,
  unsubscribeFromGame,
  invokeStop,
  on
};
